import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { type HotkeyCallback, useHotkeys } from 'react-hotkeys-hook';

import { TriangleAlertIcon } from '@air/design-icons';
import { getSavedSizeSelectionId, isSavedSizeSelectionId, type SavedSize } from '@air/feature-saved-sizes/utils';
import { Banner } from '@air/primitive-banner';

import { DimensionBar } from '~/components/DimensionBar/DimensionBar';
import { ImageSuffixFormField } from '~/components/ExportImagesModal/components/ExportImagesModalForm/components/ImageSuffixFormField';
import { ExportImageExtSelect } from '~/components/ExportImagesModal/components/ExportImagesModalForm/ExportImageExtSelect';
import { ExportImageSizeSelect } from '~/components/ExportImagesModal/components/ExportImagesModalForm/ExportImageSizeSelect';
import { type ExportImagesFormData } from '~/components/ExportImagesModal/ExportImagesModal';

const BUTTON_TARGET_SELECTOR = 'button, [role="button"]';

// Enter on a focused button already activates it; the export shortcut must not run on top of that.
const isButtonTarget = (event: KeyboardEvent) =>
  event.target instanceof Element && event.target.closest(BUTTON_TARGET_SELECTOR) !== null;

export type ExportImagesModalFormProps = {
  exportImage: (formValues: ExportImagesFormData) => void;
  imageProcessingError: boolean;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  setIsExportDisabled: (isDisabled: boolean) => void;
  sizePresets: SavedSize[];
};

export const ExportImagesModalForm = ({
  exportImage,
  imageProcessingError,
  isEditing,
  setIsEditing,
  setIsExportDisabled,
  sizePresets,
}: ExportImagesModalFormProps) => {
  const { values } = useFormikContext<ExportImagesFormData>();
  const selectedSavedSize = sizePresets.find((savedSize) => getSavedSizeSelectionId(savedSize.id) === values.size);

  const keyboardHandler = useCallback<HotkeyCallback>(() => exportImage(values), [exportImage, values]);

  useHotkeys('enter', keyboardHandler, {
    enableOnFormTags: ['INPUT'],
    enabled: !isEditing,
    ignoreEventWhen: isButtonTarget,
  });

  return (
    <>
      <div className="flex justify-between pb-4">
        <ExportImageSizeSelect setIsEditing={setIsEditing} sizePresets={sizePresets} />
      </div>
      <DimensionBar
        exactSizeAspectRatio={selectedSavedSize ? selectedSavedSize.width / selectedSavedSize.height : undefined}
        isExactSizeValue={isSavedSizeSelectionId}
        labelPlacement="inside"
        onError={setIsExportDisabled}
        setIsEditing={setIsEditing}
        className="mb-4"
      />
      <div className="flex flex-col gap-4">
        <ImageSuffixFormField setIsEditing={setIsEditing} />
        <ExportImageExtSelect setIsEditing={setIsEditing} />
      </div>
      {imageProcessingError && (
        <Banner className="mt-4 items-center" appearance="danger" prefix={<TriangleAlertIcon size="sm" />}>
          <p>Error: File cannot be processed.</p>
        </Banner>
      )}
    </>
  );
};
